import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { IMAGES } from "@/lib/content";
import { useLang } from "@/lib/i18n";
import { scrollToId } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 180]);
  const scale = useTransform(scrollY, [0, 900], [1, 1.08]);
  const { t, lang } = useLang();

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="" aria-hidden="true" className="h-[115%] w-full object-cover" />
        <div className="absolute inset-0 bg-ink/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/70" />
      </motion.div>

      <div key={lang} className="relative flex-1 flex flex-col justify-end max-w-7xl mx-auto w-full px-6 md:px-10 pt-40 pb-16">
        <motion.p
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="font-mono text-xs uppercase tracking-[0.4em] text-gold mb-8 flex items-center gap-4"
        >
          <span className="inline-block w-10 h-px bg-gold" aria-hidden="true" />
          {t.hero.kicker}
        </motion.p>

        <h1 data-testid="hero-heading" className="font-serif text-5xl sm:text-6xl lg:text-6xl xl:text-7xl leading-[1.02] text-mist mb-8">
          <MaskedLine delay={0.35}>{t.hero.line1}</MaskedLine>
          <MaskedLine delay={0.5} className="italic text-gold">
            {t.hero.line2}
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          className="text-dim text-base md:text-lg max-w-xl mb-10 leading-relaxed"
        >
          {t.hero.text}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1, ease: EASE }}
          className="flex flex-wrap items-center gap-4 mb-20"
        >
          <button
            data-testid="hero-cta-consultation"
            onClick={() => scrollToId("#contact")}
            className="group inline-flex items-center gap-3 bg-gold text-ink px-7 py-3.5 text-sm font-medium tracking-wide hover:bg-gold-light transition-colors duration-300"
          >
            {t.hero.cta}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-services"
            onClick={() => scrollToId("#services")}
            className="inline-flex items-center gap-3 border border-line text-mist px-7 py-3.5 text-sm tracking-wide hover:border-gold hover:text-gold transition-colors duration-300"
          >
            {t.hero.services}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="grid grid-cols-3 gap-6 border-t border-line/70 pt-8"
        >
          {t.hero.stats.map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <p className="font-mono text-2xl md:text-3xl text-gold">{s.value}</p>
              <p className="text-dim text-xs md:text-sm mt-1">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 right-6 md:right-10 text-dim hover:text-gold transition-colors duration-300"
        aria-label={t.hero.scroll}
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }} className="block">
          <ArrowDown size={20} />
        </motion.span>
      </motion.button>
    </section>
  );
};
